import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { collection, addDoc, query, orderBy, onSnapshot, deleteDoc, doc, getDocs, writeBatch, where } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';

function PatchNotes({ isAdmin }) {
    const [patchNotes, setPatchNotes] = useState([]);
    const [filteredNotes, setFilteredNotes] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [newPatchNote, setNewPatchNote] = useState({ version: '', content: '' });
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOption, setSearchOption] = useState('content');

    // Fetch patch notes
    useEffect(() => {
        const q = query(collection(db, 'patchNotes'), orderBy('timestamp', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notes = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setPatchNotes(notes);
            setFilteredNotes(notes);
        });
        return () => unsubscribe();
    }, []);

    // Search function
    useEffect(() => {
        if (!searchQuery.trim()) {
        setFilteredNotes(patchNotes);
        return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = patchNotes.filter(note => {
        switch (searchOption) {
            case 'version':
                return note.version.toLowerCase().includes(query);
            case 'date':
                return note.timestamp
                    ?.toDate()
                    .toLocaleDateString()
                    .toLowerCase()
                    .includes(query);
            case 'content':
                return note.content.toLowerCase().includes(query);
            default:
                return true;
        }
        });
        setFilteredNotes(filtered);
    }, [searchQuery, searchOption, patchNotes]);

    const parseVersion = (version) => {
        const matches = version.match(/v(\d+)\.(\d+)\.(\d+)/);
        if (matches) {
            return {
                major: parseInt(matches[1]),
                minor: parseInt(matches[2]),
                patch: parseInt(matches[3])
            };
        }
        return { major: 1, minor: 0, patch: 0 };
    };

    const getNextVersion = (currentVersion) => {
        const { major, minor, patch } = parseVersion(currentVersion);
        
        if (patch < 9) {
            return `v${major}.${minor}.${patch + 1}`;
        } else if (minor < 9) {
            return `v${major}.${minor + 1}.0`;
        } else {
            return `v${major + 1}.0.0`;
        }
    };

    const handleAddPatchNote = async () => {
        try {
            let nextVersion;
            if (patchNotes.length > 0) {
                const latestVersion = patchNotes[0].version;
                nextVersion = getNextVersion(latestVersion);
            } else {
                nextVersion = 'v1.0.0';
            }

            // Add the patch note
            const patchNoteRef = await addDoc(collection(db, 'patchNotes'), {
                version: nextVersion,
                content: newPatchNote.content,
                timestamp: new Date(),
            });

            // Create notifications for all users
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const batch = writeBatch(db);
            
            usersSnapshot.forEach((userDoc) => {
                const notificationRef = doc(collection(db, 'notifications'));
                batch.set(notificationRef, {
                    userId: userDoc.id,
                    message: `Version ${nextVersion} has been released. Check dashboard for details.`,
                    createdAt: new Date(),
                    read: false,
                    type: 'patchNote',
                    patchNoteId: patchNoteRef.id // Reference to the patch note
                });
            });

            await batch.commit();
            
            setNewPatchNote({ version: '', content: '' });
            setIsEditing(false);
            toast.success('Patch note added successfully!');
        } catch (error) {
            console.error('Error adding patch note:', error);
            toast.error('Failed to add patch note');
        }
    };

    const handleDeleteNote = async () => {
        try {
            // Delete the patch note
            await deleteDoc(doc(db, 'patchNotes', noteToDelete.id));

            // Delete associated notifications
            const notificationsQuery = query(
                collection(db, 'notifications'),
                where('type', '==', 'patchNote'),
                where('patchNoteId', '==', noteToDelete.id)
            );

            const notificationsSnapshot = await getDocs(notificationsQuery);
            const batch = writeBatch(db);
            
            notificationsSnapshot.forEach((doc) => {
                batch.delete(doc.ref);
            });

            await batch.commit();

            setDeleteModalOpen(false);
            setNoteToDelete(null);
            toast.success('Patch note deleted successfully!');
        } catch (error) {
            console.error('Error deleting patch note:', error);
            toast.error('Failed to delete patch note');
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="bg-gray-100 dark:bg-white/5 rounded-lg shadow-sm p-6 flex flex-col h-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Patch Notes</h2>
                    {isAdmin && !isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 text-indigo-400 hover:text-indigo-300"
                        >
                            <FaEdit className="h-5 w-5" />
                        </button>
                    )}
                </div>
            
                {/* Search Section */}
                <div className="mb-4 flex-shrink-0">
                    <div className="flex space-x-2">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FaSearch className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search Here..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                        </div>
                        <select
                            value={searchOption}
                            onChange={(e) => setSearchOption(e.target.value)}
                            className="block w-32 py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                            <option value="content">Content</option>
                            <option value="version">Version</option>
                            <option value="date">Date</option>
                        </select>
                    </div>
                </div>
            
                {/* Editing Section */}
                {isAdmin && isEditing && (
                    <div className="mb-4 flex-shrink-0">
                        <div className="text-sm text-gray-500 mb-2">
                            Next version will be: {patchNotes.length > 0 ? getNextVersion(patchNotes[0].version) : 'v1.0.0'}
                        </div>
                        <textarea
                            placeholder="Patch notes content..."
                            value={newPatchNote.content}
                            onChange={(e) => setNewPatchNote({...newPatchNote, content: e.target.value})}
                            className="w-full p-2 border rounded h-32"
                        />
                        <div className="flex space-x-2">
                            <button
                                onClick={handleAddPatchNote}
                                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            >
                                Save
                            </button>
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setNewPatchNote({ version: '', content: '' });
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            
                {/* Results Section - Now with proper scrolling */}
                <div className="flex-1 overflow-y-auto">
                    <div className="space-y-4 p-4">
                        {filteredNotes.length === 0 ? (
                            <div className="text-center text-gray-500 py-4">
                                No patch notes found matching your search.
                            </div>
                        ) : (
                            filteredNotes.map((note) => (
                                <div key={note.id} className="border-gray-800 dark:border-white border-b pb-4">
                                    <div className="flex justify-between items-start">
                                        <h3 className="font-medium text-gray-900">{note.version}</h3>
                                        {isAdmin && (
                                            <button
                                                onClick={() => {
                                                    setNoteToDelete(note);
                                                    setDeleteModalOpen(true);
                                                }}
                                                className="p-1 text-red-600 hover:text-red-800"
                                            >
                                                <FaTrash className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <p className="mt-1 mb-2 text-gray-600 whitespace-pre-line">{note.content}</p>
                                    <span className="text-sm text-gray-500">
                                        {note.timestamp?.toDate().toLocaleDateString()}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-sm mx-4">
                        <h3 className="text-lg font-medium mb-4">Delete Patch Note</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete version {noteToDelete?.version}? This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setDeleteModalOpen(false);
                                    setNoteToDelete(null);
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteNote}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PatchNotes;